import { lazy, Suspense, useEffect, useState } from "react";
import { api, getApiBase } from "./api/client";
import type { ValidateResult } from "./api/types";
import { ToastProvider, useToast, errText } from "./components/ui";
import {
  IconDashboard, IconDatabase, IconActivity, IconWallet, IconShield,
  IconOrders, IconPositions, IconChart, IconHistory, IconSettings,
  IconCandles,
} from "./components/icons";
import Overview from "./views/Overview";
import DataSources from "./views/DataSources";
import Strategies from "./views/Strategies";
import Traders from "./views/Traders";
import Settings from "./views/Settings";
import Revisions from "./views/Revisions";
import Connection from "./views/Connection";
import { Orders, Positions, Charts } from "./views/Placeholders";

const ETFAnalysis = lazy(() => import("./views/ETFAnalysis"));
const InstitutionalSignals = lazy(() => import("./views/InstitutionalSignals"));

type Id =
  | "overview" | "sources" | "strategies" | "traders" | "risk"
  | "orders" | "positions" | "signals" | "charts" | "etf-analysis" | "revisions" | "settings";

const NAV: { group: string | null; items: { id: Id; label: string; icon: JSX.Element; soon?: boolean }[] }[] = [
  { group: null, items: [{ id: "overview", label: "概览", icon: <IconDashboard /> }] },
  { group: "配置", items: [
    { id: "sources", label: "数据源", icon: <IconDatabase /> },
    { id: "strategies", label: "策略", icon: <IconActivity /> },
    { id: "traders", label: "执行器", icon: <IconWallet /> },
    { id: "risk", label: "风控 / 对账", icon: <IconShield /> },
  ]},
  { group: "交易", items: [
    { id: "orders", label: "订单管理", icon: <IconOrders />, soon: true },
    { id: "positions", label: "仓位管理", icon: <IconPositions />, soon: true },
  ]},
  { group: "分析", items: [
    { id: "signals", label: "机构信号", icon: <IconActivity /> },
    { id: "charts", label: "指标图表", icon: <IconChart />, soon: true },
    { id: "etf-analysis", label: "ETF分析", icon: <IconCandles /> },
  ]},
  { group: "系统", items: [
    { id: "revisions", label: "版本历史", icon: <IconHistory /> },
    { id: "settings", label: "连接设置", icon: <IconSettings /> },
  ]},
];
const LABEL: Record<Id, string> = Object.fromEntries(
  NAV.flatMap((g) => g.items.map((i) => [i.id, i.label])),
) as Record<Id, string>;

export default function App() {
  return (
    <ToastProvider>
      <Shell />
    </ToastProvider>
  );
}

function Shell() {
  const notify = useToast();
  const [active, setActive] = useState<Id>("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const [vr, setVr] = useState<ValidateResult | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState<boolean | null>(null);
  const bump = () => setRefreshKey((k) => k + 1);

  const validate = async () => {
    try {
      const r = await api.validate();
      setVr(r); setOnline(true);
    } catch (e) { setVr(null); setOnline(false); void e; }
  };
  useEffect(() => { validate(); }, [refreshKey]);

  const apply = async () => {
    const note = prompt("应用备注（可选）", "") ?? "";
    setBusy(true);
    try {
      const r = await api.apply(note);
      if (r.ok) notify({ kind: "ok", msg: `应用成功：${r.applied.join("、") || "无变化"}` });
      else notify({ kind: "error", msg: `应用失败：${r.errors.join("；")}` });
      bump();
    } catch (e) { notify({ kind: "error", msg: errText(e) }); }
    finally { setBusy(false); }
  };

  const hasErr = vr ? !vr.ok : false;
  const dirty = vr?.has_dirty ?? false;

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <div className="logo">沙</div>
          <div>
            <div className="name">量化控制面</div>
            <div className="sub">异构频率沙漏系统</div>
          </div>
        </div>
        <nav className="nav">
          {NAV.map((g, gi) => (
            <div className="nav-group" key={gi}>
              {g.group && <div className="label">{g.group}</div>}
              {g.items.map((it) => (
                <div key={it.id} className={`nav-item ${active === it.id ? "active" : ""}`} onClick={() => setActive(it.id)}>
                  {it.icon}<span>{it.label}</span>{it.soon && <span className="soon">规划</span>}
                </div>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="conn-status">
            <span className={`dot ${online === null ? "off" : online ? "on" : "err"}`} />
            <span className="mono" title={getApiBase()}>{online === null ? "连接中…" : online ? "已连接" : "未连接"}</span>
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <h2>{LABEL[active]}</h2>
          <span className="path mono">/ {active}</span>
          <div className="grow" />
          <div className="applybar">
            {dirty
              ? <span className="badge dirty" onClick={() => setShowIssues(!showIssues)} style={{ cursor: "pointer" }}>● 有未应用草稿</span>
              : <span className="badge on">● 已同步</span>}
            <button className="sm" onClick={validate} disabled={busy}>校验</button>
            <button className="primary sm" onClick={apply} disabled={busy || hasErr} title={hasErr ? "校验未通过" : "热应用到运行态"}>
              {hasErr ? "校验未通过" : "应用变更"}
            </button>
            <button className="ghost sm" onClick={() => window.location.assign("/cdn-cgi/access/logout")}>退出</button>
          </div>
        </div>

        <div className="content">
          <div className="content-inner">
            {vr && vr.issues.length > 0 && showIssues && (
              <div className="panel">
                <div className="panel-head"><span className="title">校验结果</span><span className="spacer" /><button className="ghost sm" onClick={() => setShowIssues(false)}>收起</button></div>
                <div className="issues">
                  {vr.issues.map((i, k) => (
                    <div key={k} className={`issue ${i.level}`}><b>{i.target}</b><span>{i.message}</span></div>
                  ))}
                </div>
              </div>
            )}

            {active === "overview" && <Overview onGo={(id) => setActive(id as Id)} />}
            {active === "sources" && <DataSources onChanged={bump} />}
            {active === "strategies" && <Strategies onChanged={bump} />}
            {active === "traders" && <Traders onChanged={bump} />}
            {active === "risk" && <Settings onChanged={bump} />}
            {active === "revisions" && <Revisions key={refreshKey} />}
            {active === "settings" && <Connection onSaved={bump} />}
            {active === "orders" && <Orders />}
            {active === "positions" && <Positions />}
            {active === "charts" && <Charts />}
            {active === "signals" && (
              <Suspense fallback={<div className="panel"><div className="empty">正在接入实时信号…</div></div>}>
                <InstitutionalSignals />
              </Suspense>
            )}
            {active === "etf-analysis" && (
              <Suspense fallback={<div className="panel"><div className="empty">正在加载 ETF 分析模块…</div></div>}>
                <ETFAnalysis />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
